import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { AddeparInventoryStatus } from "./addeparInventoryUtils";
import { getEntityDateColumn } from "../../utils/entityDateColumn";

const STATUS_LABELS = {
  [AddeparInventoryStatus.PENDING]: "Pending",
  [AddeparInventoryStatus.ONBOARDED]: "Onboarded",
  [AddeparInventoryStatus.IGNORED]: "Ignored",
};

const statusChipSx = (status, theme) => {
  if (status === AddeparInventoryStatus.ONBOARDED) {
    return {
      color: theme.palette.mode === "dark" ? "#81c784" : "#2e7d32",
      border: `1px solid ${
        theme.palette.mode === "dark" ? "#66bb6a" : "#c8e6c9"
      }`,
    };
  }
  if (status === AddeparInventoryStatus.IGNORED) {
    return {
      color: theme.palette.mode === "dark" ? "#bdbdbd" : "#616161",
      border: `1px solid ${
        theme.palette.mode === "dark" ? "#757575" : "#e0e0e0"
      }`,
    };
  }
  return {
    color: theme.palette.mode === "dark" ? "#ffb74d" : "#ef6c00",
    border: `1px solid ${
      theme.palette.mode === "dark" ? "#ff9800" : "#ffe0b2"
    }`,
  };
};

const AddeparInventoryTable = ({ inventory, updatingId, onIgnore, onImport }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 1000,
  });

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(inventory) ? inventory : [];
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter((row) => {
      return (
        String(row.name || "").toLowerCase().includes(q) ||
        String(row.accountNumber || "").toLowerCase().includes(q) ||
        String(STATUS_LABELS[row.status] || row.status || "")
          .toLowerCase()
          .includes(q) ||
        String(row.createdAt || "").toLowerCase().includes(q)
      );
    });
  }, [inventory, searchTerm]);

  const rows = useMemo(
    () =>
      filteredRows.map((row, index) => ({
        ...row,
        id: row.addeparInventoryId || row.id || index,
      })),
    [filteredRows]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: "accountNumber",
        headerName: "Account Number",
        flex: 1.2,
        minWidth: 150,
      },
      getEntityDateColumn({
        field: "createdAt",
        headerName: "Created",
        flex: 1,
        minWidth: 140,
      }),
      {
        field: "status",
        headerName: "Status",
        flex: 0.9,
        minWidth: 120,
        renderCell: (params) => {
          const status = params.value || AddeparInventoryStatus.PENDING;
          return (
            <Chip
              label={STATUS_LABELS[status] || status}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                ...statusChipSx(status, theme),
              }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 280,
        minWidth: 260,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const status = params.row.status || AddeparInventoryStatus.PENDING;
          if (status !== AddeparInventoryStatus.PENDING) return null;

          const rowId =
            params.row.addeparInventoryId || params.row.addeparEntityId;
          const isUpdating = Boolean(updatingId) && updatingId === rowId;
          const hasAccountNumber = Boolean(
            String(params.row.accountNumber ?? "").trim()
          );

          return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                size="small"
                variant="outlined"
                disabled={Boolean(updatingId)}
                onClick={() => onIgnore?.(params.row)}
                sx={{
                  textTransform: "none",
                  borderRadius: 1,
                  color: "#ef5350",
                  border: "1px solid #ef5350",
                  background: "transparent",
                  "&:hover": {
                    color: "#fff",
                    border: "1px solid #ef5350",
                    background: "#ef5350",
                    boxShadow: "0 4px 12px rgba(239, 83, 80, 0.35)",
                  },
                }}
              >
                {isUpdating ? "..." : "Ignore"}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={Boolean(updatingId) || !hasAccountNumber}
                onClick={() => onImport?.(params.row)}
                title={
                  hasAccountNumber
                    ? undefined
                    : "Account number is required to import"
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 1,
                  color: "#fff",
                  background: "#43a047",
                  border: "none",
                  boxShadow: "none",
                  "&:hover": {
                    background: "#2e7d32",
                    boxShadow: "0 4px 12px rgba(67, 160, 71, 0.4)",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255, 255, 255, 0.7)",
                    background: "rgba(67, 160, 71, 0.35)",
                  },
                }}
              >
                {isUpdating ? "..." : "Add to Vantage"}
              </Button>
            </Box>
          );
        },
      },
    ],
    [theme, updatingId, onIgnore, onImport]
  );

  const tableBg =
    theme.palette.mode === "dark"
      ? theme.palette.grey[100]
      : theme.palette.background.paper;

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <TextField
          size="small"
          placeholder="Search inventory..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 300,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) =>
          setPaginationModel({
            page: model.page,
            pageSize: Math.min(model.pageSize, 100),
          })
        }
        pageSizeOptions={[100,500,1000]}
        pagination
        density="compact"
        disableRowSelectionOnClick
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          backgroundColor: tableBg,
          "& .MuiDataGrid-main": {
            backgroundColor: "inherit",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
            backgroundColor:
              theme.palette.mode === "dark" ? theme.palette.grey[100] : undefined,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.grey[100],
            borderTop: `1px solid ${theme.palette.divider}`,
          },
        }}
      />
    </Box>
  );
};

export default AddeparInventoryTable;
